<template>
  <base-chart
    title="Funding Analytics"
    :options="chartOptions"
    :height="height"
    @chart-click="handleBarClick"
  />
</template>

<script>
import BaseChart from './BaseChart.vue'
import ApiMixin from '@/mixins/api'

export default {
  name: 'FundingChart',
  components: {
    BaseChart
  },
  mixins: [ApiMixin],
  props: {
    height: {
      type: String,
      default: '400px'
    },
    period: {
      type: String,
      default: 'weekly',
      validator: (value) => ['daily', 'weekly', 'monthly'].includes(value)
    }
  },
  data () {
    return {
      loading: false,
      fundingData: []
    }
  },
  computed: {
    chartOptions () {
      return {
        title: {
          show: false
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          },
          formatter: (params) => {
            const param = params[0]
            return `${param.name}<br/>Funding: $${param.value.toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}`
          }
        },
        xAxis: {
          type: 'category',
          data: this.periods,
          axisLabel: {
            rotate: 45,
            interval: 0
          }
        },
        yAxis: {
          type: 'value',
          axisLabel: {
            formatter: (value) => {
              return '$' + (value / 1000).toFixed(0) + 'K'
            }
          }
        },
        series: [
          {
            name: 'Funding Amount',
            type: 'bar',
            data: this.amounts,
            itemStyle: {
              borderRadius: [4, 4, 0, 0]
            },
            emphasis: {
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: 'rgba(0, 0, 0, 0.5)'
              }
            },
            label: {
              show: false,
              position: 'top',
              formatter: (params) => {
                return '$' + (params.value / 1000).toFixed(0) + 'K'
              }
            }
          }
        ]
      }
    },
    periods () {
      if (this.fundingData.length > 0) {
        return this.fundingData.map(f => f.period)
      }
      return this.generateSamplePeriods()
    },
    amounts () {
      if (this.fundingData.length > 0) {
        return this.fundingData.map(f => f.amount)
      }
      return this.generateSampleData()
    }
  },
  mounted () {
    this.fetchFundingData()
  },
  watch: {
    period () {
      this.fetchFundingData()
    }
  },
  methods: {
    async fetchFundingData () {
      this.loading = true
      try {
        const response = await this.api({
          url: '/analytics/funding',
          method: 'get',
          params: {
            period: this.period
          }
        })

        if (response.data && response.data.funding) {
          this.fundingData = response.data.funding
        }
      } catch (err) {
        console.error('Error fetching funding data:', err)
      } finally {
        this.loading = false
      }
    },
    handleBarClick (params) {
      const period = params.name
      const amount = params.value
      this.$emit('drill-down', { period, amount })
    },
    generateSamplePeriods () {
      if (this.period === 'daily') {
        const periods = []
        const today = new Date()
        for (let i = 6; i >= 0; i--) {
          const date = new Date(today)
          date.setDate(date.getDate() - i)
          const month = date.getMonth() + 1
          const day = date.getDate()
          periods.push(`${month}/${day}`)
        }
        return periods
      } else if (this.period === 'weekly') {
        return ['Week 1', 'Week 2', 'Week 3', 'Week 4', 'Week 5']
      } else {
        return ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun']
      }
    },
    generateSampleData () {
      const baseAmount = this.period === 'daily' ? 25000 : this.period === 'weekly' ? 125000 : 500000
      const count = this.period === 'daily' ? 7 : this.period === 'weekly' ? 5 : 6
      const data = []
      for (let i = 0; i < count; i++) {
        const variance = Math.random() * 0.4 - 0.2
        data.push(Math.round(baseAmount * (1 + variance)))
      }
      return data
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/charts/FundingChart.vue